<script setup lang="ts">
import { ref, onMounted, inject, Ref, computed } from 'vue'
import { useCollections } from '@/services/Collections'
import { Collection } from '@/types/Collections'
import { Member } from '@/types/Member'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { PlusCircleIcon, EllipsisVerticalIcon } from '@heroicons/vue/24/solid'
import { Folder, Search } from 'lucide-vue-next'
import { toast } from 'vue-sonner'

const { collections, loading, error, getCollections, createCollection, deleteCollection } = useCollections()
const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)

const selectedTab = ref('all')
const searchQuery = ref('')
const showCreateDialog = ref(false)
const newCollection = ref({
	name: '',
	description: '',
	type: 'manual' as string,
})

const filteredCollections = computed(() => {
	let result = collections.value

	if (selectedTab.value !== 'all') {
		result = result.filter((c) => c.type === selectedTab.value)
	}

	if (searchQuery.value) {
		const query = searchQuery.value.toLowerCase()
		result = result.filter((c) =>
			c.name.toLowerCase().includes(query) ||
			(c.description && c.description.toLowerCase().includes(query))
		)
	}

	return result
})

const manualCount = computed(() => collections.value.filter((c) => c.type === 'manual').length)
const searchCount = computed(() => collections.value.filter((c) => c.type === 'search').length)

const handleCreate = async () => {
	if (!vhostId || !vhostId.value || !newCollection.value.name) {
		return
	}

	const result = await createCollection(
		vhostId.value,
		newCollection.value.name,
		newCollection.value.description,
		newCollection.value.type,
	)

	if (result) {
		toast('Collection Created', { description: `"${newCollection.value.name}" has been created.` })
		newCollection.value = { name: '', description: '', type: 'manual' }
		showCreateDialog.value = false
		await getCollections(vhostId.value)
	} else {
		toast('Error', { description: 'Failed to create collection.' })
	}
}

const handleDelete = async (collection: Collection) => {
	const success = await deleteCollection(collection.id)
	if (success && vhostId && vhostId.value) {
		toast('Collection Deleted', { description: `"${collection.name}" has been removed.` })
		await getCollections(vhostId.value)
	}
}

onMounted(async () => {
	if (vhostId && vhostId.value) {
		await getCollections(vhostId.value)
	}
})
</script>

<template>
	<div class="p-4 space-y-4">
		<div class="grid gap-4 md:grid-cols-3">
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Total Collections</CardTitle>
					<Folder class="h-5 w-5 text-muted-foreground" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ collections.length }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Manual Collections</CardTitle>
					<Folder class="h-5 w-5 text-muted-foreground" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ manualCount }}</div>
					<p class="text-xs text-muted-foreground">Curated by hand</p>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Smart Collections</CardTitle>
					<Search class="h-5 w-5 text-muted-foreground" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ searchCount }}</div>
					<p class="text-xs text-muted-foreground">Auto-updating search-based</p>
				</CardContent>
			</Card>
		</div>

		<Card>
			<CardHeader>
				<div class="flex justify-between items-center">
					<div>
						<CardTitle class="text-lg font-normal">Collections</CardTitle>
						<CardDescription>Organize your media into curated collections</CardDescription>
					</div>
					<Dialog v-model:open="showCreateDialog">
						<DialogTrigger as-child>
							<Button>
								<PlusCircleIcon class="h-4 w-4 mr-2" />
								New Collection
							</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Create Collection</DialogTitle>
								<DialogDescription>Create a new collection to organize your media</DialogDescription>
							</DialogHeader>
							<div class="space-y-4 py-4">
								<div class="space-y-2">
									<Label for="name">Name</Label>
									<Input id="name" v-model="newCollection.name" placeholder="Collection name" />
								</div>
								<div class="space-y-2">
									<Label for="description">Description</Label>
									<Textarea id="description" v-model="newCollection.description" placeholder="Optional description" />
								</div>
								<div class="space-y-2">
									<Label for="type">Type</Label>
									<Select v-model="newCollection.type">
										<SelectTrigger>
											<SelectValue placeholder="Select type" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="manual">Manual Collection</SelectItem>
											<SelectItem value="search">Smart Collection (Search-based)</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>
							<DialogFooter>
								<Button variant="outline" @click="showCreateDialog = false">Cancel</Button>
								<Button @click="handleCreate" :disabled="!newCollection.name">Create</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				</div>
			</CardHeader>
			<CardContent>
				<div class="space-y-4">
					<div class="flex justify-between items-center">
						<Tabs v-model="selectedTab">
							<TabsList>
								<TabsTrigger value="all">All ({{ collections.length }})</TabsTrigger>
								<TabsTrigger value="manual">Manual ({{ manualCount }})</TabsTrigger>
								<TabsTrigger value="search">Smart ({{ searchCount }})</TabsTrigger>
							</TabsList>
						</Tabs>
						<Input v-model="searchQuery" placeholder="Search collections..." class="max-w-xs" />
					</div>

					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Type</TableHead>
								<TableHead class="text-center">Media</TableHead>
								<TableHead>Created</TableHead>
								<TableHead>Updated</TableHead>
								<TableHead class="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<template v-if="filteredCollections.length > 0">
								<TableRow v-for="collection in filteredCollections" :key="collection.id" class="cursor-pointer hover:bg-muted/50">
									<TableCell class="font-medium">
										<div class="flex items-center space-x-3">
											<div v-if="collection.thumbUrl" class="w-10 h-10 rounded bg-muted overflow-hidden">
												<img :src="collection.thumbUrl" class="w-full h-full object-cover" :alt="collection.name" />
											</div>
											<div v-else class="w-10 h-10 rounded bg-muted flex items-center justify-center">
												<Folder class="h-5 w-5 text-muted-foreground" />
											</div>
											<div>
												<div>{{ collection.name }}</div>
												<div v-if="collection.description" class="text-xs text-muted-foreground">{{ collection.description }}</div>
											</div>
										</div>
									</TableCell>
									<TableCell>
										<Badge :variant="collection.type === 'manual' ? 'default' : 'secondary'">
											{{ collection.type === 'manual' ? 'Manual' : 'Smart' }}
										</Badge>
									</TableCell>
									<TableCell class="text-center">{{ collection.mediaCount || 0 }}</TableCell>
									<TableCell class="text-sm text-muted-foreground">{{ collection.createdDate || '-' }}</TableCell>
									<TableCell class="text-sm text-muted-foreground">{{ collection.updatedDate || '-' }}</TableCell>
									<TableCell class="text-right">
										<DropdownMenu>
											<DropdownMenuTrigger as-child>
												<Button variant="ghost" size="sm">
													<EllipsisVerticalIcon class="h-4 w-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent>
												<DropdownMenuItem>Edit</DropdownMenuItem>
												<DropdownMenuItem>Manage Items</DropdownMenuItem>
												<DropdownMenuItem>Duplicate</DropdownMenuItem>
												<DropdownMenuItem class="text-destructive" @click="handleDelete(collection)">Delete</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							</template>
							<TableRow v-else>
								<TableCell colspan="6" class="h-24 text-center text-muted-foreground">
									<div v-if="loading">Loading collections...</div>
									<div v-else>No collections found. Create your first collection to get started.</div>
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</div>
			</CardContent>
		</Card>
	</div>
</template>
